import type * as lazyIndex from './lazyIndex';
import { LazyImport, LazyModule, LazyAction, registerLazyOrchestrator } from 'owa-bundling';
import { fetchDumpsterFolder } from './actions/fetchDumpsterFolder';
import { fetchArchiveDumpsterFolder } from './actions/fetchArchiveDumpsterFolder';

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "Folders" */ './lazyIndex'),
    {
        name: 'Folders',
    }
);

// Export lazy loaded actions
export const lazyGetArchiveFolders: LazyImport<
    typeof lazyIndex.getArchiveFolders,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getArchiveFolders);

export const lazyGetFolderPermissions: LazyImport<
    typeof lazyIndex.getFolderPermissions,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getFolderPermissions);

export const lazyUpdateFolderPermissions: LazyImport<
    typeof lazyIndex.updateFolderPermissions,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.updateFolderPermissions);

export const lazyUpdateFolderPermissionsInFolderStore: LazyAction<
    typeof lazyIndex.updateFolderPermissionsInStoreMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.updateFolderPermissionsInStoreMutator);

export const lazyFetchArchiveDumpsterFolder: LazyAction<
    typeof lazyIndex.fetchArchiveDumpsterFolder,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.fetchArchiveDumpsterFolder);
export const lazyFetchFavoriteFolders: LazyImport<
    typeof lazyIndex.fetchFavoriteFolders,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.fetchFavoriteFolders);

// Export utils
export const lazyArePermissionArraysEqual: LazyImport<
    typeof lazyIndex.arePermissionArraysEqual,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.arePermissionArraysEqual);

export const lazyArePermissionsEqual: LazyImport<
    typeof lazyIndex.arePermissionsEqual,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.arePermissionsEqual);

export const lazyGetSharedFoldersForSpecificRoot: LazyImport<
    typeof lazyIndex.getSharedFolders,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getSharedFolders);

export const lazyUpdateFolder: LazyAction<typeof lazyIndex.updateFolder, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.updateFolder);

export const lazyFetchMoreFolders: LazyImport<
    typeof lazyIndex.fetchMoreFolders,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.fetchMoreFolders);

export const lazyCreateNewFolderInStore: LazyAction<
    typeof lazyIndex.createNewFolderInStoreMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.createNewFolderInStoreMutator);

export const lazyDeleteFoldersFromFolderTable: LazyAction<
    typeof lazyIndex.deleteFoldersFromFolderTableMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.deleteFoldersFromFolderTableMutator);
export const lazyRemoveFolderFromParentFolder: LazyAction<
    typeof lazyIndex.removeFolderFromParentFolderMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeFolderFromParentFolderMutator);
export const lazyMoveFolderToParentFolder: LazyAction<
    typeof lazyIndex.moveFolderToParentFolderMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.moveFolderToParentFolderMutator);
export const lazyFetchFavoriteFoldersResponse: LazyAction<
    typeof lazyIndex.fetchFavoriteFoldersResponseMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.fetchFavoriteFoldersResponseMutator);

export const lazyOutlookFavoritePersonaSearchFoldersLoadedMutator: LazyAction<
    typeof lazyIndex.outlookFavoritePersonaSearchFoldersLoadedMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.outlookFavoritePersonaSearchFoldersLoadedMutator);

/**
 * Makes a service call to fetch the primary mail folders.
 */
export const lazyFetchPrimaryMailFolders: LazyImport<
    typeof lazyIndex.fetchPrimaryMailFolders,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.fetchPrimaryMailFolders);

export const lazyFetchDumpsterFolder: LazyAction<
    typeof lazyIndex.fetchDumpsterFolder,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.fetchDumpsterFolder);

export const lazyFetchSearchFolders: LazyAction<
    typeof lazyIndex.fetchSearchFolders,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.fetchSearchFolders);

export const lazyGetChildFolderIds: LazyImport<
    typeof lazyIndex.getChildFolderIds,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getChildFolderIds);

export const lazyGetChildFolderNames: LazyImport<
    typeof lazyIndex.getChildFolderNames,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getChildFolderNames);

export const lazyUpdateFoldersParentFolder: LazyImport<
    typeof lazyIndex.updateFoldersParentFolder,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.updateFoldersParentFolder);

export const lazyUpdateFolderId: LazyAction<
    typeof lazyIndex.updateFolderIdMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.updateFolderIdMutator);

export const lazyToggleIsAlphabetizeFoldersEnabled: LazyAction<
    typeof lazyIndex.toggleIsAlphabetizeFoldersEnabled,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.toggleIsAlphabetizeFoldersEnabled);

export const lazyCleanupSearchFolders: LazyAction<
    typeof lazyIndex.cleanupSearchFolders,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.cleanupSearchFolders);

export const lazyRemoveExpiredSearchFolderMutator: LazyAction<
    typeof lazyIndex.removeExpiredSearchFolderMutator,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.removeExpiredSearchFolderMutator);

export const lazyDetectExpiredSearchFoldersFromDefinitions: LazyImport<
    typeof lazyIndex.detectExpiredSearchFoldersFromDefinitions,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.detectExpiredSearchFoldersFromDefinitions);

// Lazy orchestrators
registerLazyOrchestrator(fetchDumpsterFolder, lazyModule, m => m.fetchDumpsterFolderOrchestrator);
registerLazyOrchestrator(
    fetchArchiveDumpsterFolder,
    lazyModule,
    m => m.fetchArchiveDumpsterFolderOrchestrator
);
